<template>
  <div class="form-container">
    <v-card class="form-card" elevation="2">
      <!-- Cabeçalho -->
      <v-card-title class="form-header">
        <span class="form-title">{{ isEditMode ? 'Editar Elemento Item' : 'Cadastro de Elemento Item' }}</span>
        <v-checkbox
          v-model="formData.ativo"
          label="Ativo"
          color="primary"
          hide-details
          class="form-checkbox"
        />
      </v-card-title>

      <!-- Formulário -->
      <v-card-text class="form-content">
        <v-form ref="form" v-model="isFormValid">
          <!-- Linha 1: Exercício -->
          <v-row class="form-row">
            <v-col cols="12" sm="6" md="3" class="pa-1">
              <div class="filter-label">Exercício</div>
              <v-select
                v-model="formData.exercicio"
                :items="exerciciosOptions"
                outlined
                dense
                :rules="[rules.required]"
                placeholder="Selecione um exercício"
                class="form-field"
                hide-details="auto"
                clearable
              />
            </v-col>
          </v-row>

          <!-- Linha 2: Código do Elemento Item e Descrição -->
          <v-row class="form-row">
            <v-col cols="12" sm="6" md="3" class="pa-1">
              <div class="filter-label">Código Elemento Item</div>
              <v-text-field
                v-model="formData.codigoElementoItem"
                type="text"
                outlined
                dense
                :rules="[rules.required, rules.numericOnly]"
                placeholder="Digite o código"
                class="form-field"
                hide-details="auto"
                @input="onNumericInput('codigoElementoItem', $event)"
                @keypress="onlyNumbers"
                @paste="onPaste"
              />
            </v-col>
            <v-col cols="12" sm="6" md="9" class="pa-1">
              <div class="filter-label">Descrição</div>
              <v-text-field
                v-model="formData.descricao"
                outlined
                dense
                :rules="[rules.required]"
                placeholder="Digite a descrição"
                class="form-field"
                hide-details="auto"
              />
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <!-- Rodapé com Botões -->
      <v-card-actions class="form-actions">
        <v-btn
          @click="handleCancel"
          class="action-btn cancel-btn grey-btn"
        >
          Cancelar
        </v-btn>
        <v-btn
          @click="handleSave"
          color="primary"
          class="action-btn save-btn"
          :loading="isSaving"
        >
          <v-icon left>mdi-content-save</v-icon>
          Salvar
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Snackbar para mensagens -->
    <v-snackbar
      v-model="showSnackbar"
      :color="snackbarColor"
      :timeout="3000"
      top
    >
      {{ snackbarMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click="showSnackbar = false"
        >
          Fechar
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { ExercicioService } from "../../services/ExercicioService";

export default {
  name: "ElementoItemForm",
  props: {
    isEditMode: {
      type: Boolean,
      default: false
    },
    elementoItemId: {
      type: [String, Number],
      default: null
    },
    initialData: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      formData: {
        exercicio: '',
        codigoElementoItem: '',
        descricao: '',
        ativo: true // Checkbox "Ativo" marcado por padrão
      },
      exerciciosOptions: [],
      isFormValid: false,
      isSaving: false,
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success',
      rules: {
        required: v => !!v || 'Por favor, preencha todos os campos obrigatórios',
        numericOnly: v => /^\d*$/.test(v) || 'Apenas números são permitidos'
      }
    };
  },
  watch: {
    initialData: {
      handler(newData) {
        if (newData && Object.keys(newData).length > 0) {
          this.formData = { ...this.formData, ...newData };
        }
      },
      immediate: true
    }
  },
  async created() {
    await this.loadExercicios();
  },
  methods: {
    async loadExercicios() {
      try {
        const response = await ExercicioService.select();
        if (response && response.data) {
          this.exerciciosOptions = response.data.map(exercicio => ({
            text: exercicio.ano,
            value: exercicio.ano
          }));
          this.exerciciosOptions.unshift({
            text: "Selecione",
            value: ""
          });
        }
      } catch (error) {
        console.error('Erro ao carregar exercícios:', error);
        this.showErrorMessage('Erro ao carregar exercícios');
      }
    },
    
    async handleSave() {
      if (!this.$refs.form.validate()) {
        this.showErrorMessage('Por favor, preencha todos os campos obrigatórios');
        return;
      }

      this.isSaving = true;
      
      try {
        // Emitir evento com dados do formulário e aguardar resposta
        this.$emit('save', {
          exercicio: this.formData.exercicio,
          codigoElementoItem: this.formData.codigoElementoItem,
          descricao: this.formData.descricao,
          ativo: this.formData.ativo
        });

        // Registrar handlers para sucesso e erro
        this.$once('elemento-item-salvo', () => {
          if (!this.isEditMode) {
            this.showSuccessMessage('Elemento Item salvo com sucesso!');
            this.resetForm();
          }
        });

        this.$once('save-error', (errors) => {
          // Mostrar a primeira mensagem de erro encontrada
          const firstError = Object.values(errors)[0];
          if (Array.isArray(firstError)) {
            this.showErrorMessage(firstError[0]);
          } else {
            this.showErrorMessage(firstError || 'Erro ao salvar Elemento Item');
          }
          // Não resetamos o form em caso de erro
          this.isSaving = false;
        });

      } catch (error) {
        console.error('Erro ao salvar:', error);
        this.showErrorMessage('Erro ao salvar Elemento Item');
        this.isSaving = false;
      }
    },
    
    handleCancel() {
      this.$emit('cancel');
    },
    
    resetForm() {
      this.formData = {
        exercicio: '',
        codigoElementoItem: '',
        descricao: '',
        ativo: true // Manter checkbox "Ativo" selecionado
      };
      this.$refs.form.resetValidation();
    },
    
    showSuccessMessage(message) {
      this.snackbarMessage = message;
      this.snackbarColor = 'success';
      this.showSnackbar = true;
    },
    
    showErrorMessage(message) {
      this.snackbarMessage = message;
      this.snackbarColor = 'error';
      this.showSnackbar = true;
    },
    // Método para permitir apenas números na digitação
    onlyNumbers(event) {
      const charCode = event.which ? event.which : event.keyCode;
      
      // Bloquear explicitamente pontos e outros caracteres especiais
      if (charCode === 190 || charCode === 110 || charCode === 188 || charCode === 46) {
        event.preventDefault();
        return false;
      }
      
      // Permitir apenas números (48-57) e teclas especiais de navegação
      if (charCode < 48 || charCode > 57) {
        // Permitir apenas teclas especiais: backspace (8), tab (9), enter (13), setas (37-40)
        if (charCode !== 8 && charCode !== 9 && charCode !== 13 && 
            charCode !== 37 && charCode !== 38 && charCode !== 39 && charCode !== 40) {
          event.preventDefault();
          return false;
        }
      }
      
      return true;
    },
    // Método para interceptar colagem e filtrar apenas números
    onPaste(event) {
      event.preventDefault();
      const pastedText = (event.clipboardData || window.clipboardData).getData('text');
      const numericOnly = pastedText.replace(/[^0-9]/g, '');
      this.formData.codigoElementoItem = numericOnly;
    },
    // Método para filtrar entrada e manter apenas números
    onNumericInput(fieldName, value) {
      // Remove qualquer caractere que não seja número
      const numericValue = value.replace(/[^0-9]/g, '');
      this.formData[fieldName] = numericValue;
    }
  }
};
</script>

<style scoped>
.form-container {
  padding: 20px;
}

.form-card {
  border-radius: 12px;
  overflow: hidden;
}

.form-header {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-bottom: 2px solid #e5e7eb;
  padding: 20px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.form-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
}

.form-checkbox {
  margin: 0;
}

.form-content {
  padding: 24px;
}

.form-row {
  margin: 0;
}

.form-field {
  margin-bottom: 16px;
}

.form-actions {
  background-color: #f8fafc;
  border-top: 1px solid #e5e7eb;
  padding: 20px 24px;
  display: flex;
  justify-content: center;
  gap: 16px;
}

.action-btn {
  min-width: 120px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-radius: 8px;
  padding: 12px 24px;
}

.cancel-btn {
  border-color: #6b7280;
  color: #6b7280;
}

.save-btn {
  background-color: #16a34a;
  border-color: #16a34a;
}

.save-btn:hover {
  background-color: #15803d;
}

/* Responsividade */
@media (max-width: 960px) {
  .form-container {
    padding: 16px;
  }
  
  .form-header {
    padding: 16px 20px;
  }
  
  .form-content {
    padding: 20px;
  }
  
  .form-actions {
    padding: 16px 20px;
  }
}

@media (max-width: 600px) {
  .form-container {
    padding: 12px;
  }
  
  .form-header {
    padding: 12px 16px;
    flex-direction: column;
    gap: 16px;
    text-align: center;
  }
  
  .form-content {
    padding: 16px;
  }
  
  .form-actions {
    padding: 12px 16px;
    flex-direction: column;
    gap: 12px;
  }
  
  .action-btn {
    width: 100%;
  }
}
</style>
